import React from 'react'
import { mdx } from '@mdx-js/react'
/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Layout from '../../../components/docs/Layout';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  description: 'Learn about using sample credentials in the Plaid Sandbox environment. The Sandbox allows you to simulate situations such as MFA, reCAPTCHA, and Item creation errors.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Sandbox test credentials' subtitle='Simulate different flows using Sandbox credentials' mdxType="Header" />
    <Callout mdxType="Callout">
      <p>{`You can browse the test users on this page, and create and manage your own, from the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/sandbox"
        }}>{`Sandbox`}</a>{` tool in the Plaid Dashboard. For more details, see `}<a parentName="p" {...{
          "href": "/docs/sandbox/user-custom/#customizing-account-data-using-the-sandbox-studio"
        }}>{`Customizing account data using the Sandbox Studio`}</a>{`.`}</p>
    </Callout>
    <Callout mdxType="Callout">
      <p>{`When using special Sandbox test credentials described on this page (other than `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` / `}<inlineCode parentName="p">{`pass_good`}</inlineCode>{`), use the `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` flow or a non-OAuth test institution, such as First Platypus Bank (`}<inlineCode parentName="p">{`ins_109508`}</inlineCode>{`). Special test credentials may be ignored when using the Sandbox Link OAuth flow.`}</p>
    </Callout>
    <h4 {...{
      "id": "sandbox-simple-test-credentials"
    }}>{`Sandbox simple test credentials`}</h4>
    <p>{`Use the username `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` and password `}<inlineCode parentName="p">{`pass_good`}</inlineCode>{`.`}</p>
    <p>{`These provide basic account access to most Plaid products. Certain Plaid products also have specialized credentials that can be used for more advanced testing or for getting more useful and realistic testing data. These specialized credentials can be found below.`}</p>
    <h4 {...{
      "id": "transactions-testing-credentials"
    }}>{`Transactions testing credentials`}</h4>
    <p>{`Use the username `}<inlineCode parentName="p">{`user_transactions_dynamic`}</inlineCode>{` and any non-blank password to test checking, credit card, and loan account transactions. For persona-based testing, use `}<inlineCode parentName="p">{`user_ewa_user`}</inlineCode>{`, `}<inlineCode parentName="p">{`user_yuppie`}</inlineCode>{`, or `}<inlineCode parentName="p">{`user_small_business`}</inlineCode>{`.`}</p>
    <p>{`These credentials have realistic transaction history and may update transactions and fire webhooks when `}<inlineCode parentName="p">{`/transactions/refresh`}</inlineCode>{` is called. For more details, including information on how often new transactions are triggered or how to add your own custom transactions, see `}<a parentName="p" {...{
        "href": "/docs/transactions/#testing-transactions-in-sandbox"
      }}>{`Testing Transactions in Sandbox`}</a>{` and `}<a parentName="p" {...{
        "href": "/docs/transactions/transactions-data/#testing-pending-and-posted-transactions"
      }}>{`testing pending and posted transactions`}</a>{`.`}</p>
    <h4 {...{
      "id": "auth-micro-deposit-testing-credentials"
    }}>{`Auth micro-deposit testing credentials`}</h4>
    <p>{`Use the username `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` and the password `}<inlineCode parentName="p">{`microdeposits_good`}</inlineCode>{`.`}</p>
    <p>{`For more details, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/institutions/#institution-details-for-auth-testing"
      }}>{`Institution details for Auth testing`}</a>{`. `}</p>
    <h4 {...{
      "id": "credit-and-income-testing-credentials"
    }}>{`Credit and Income testing credentials`}</h4>
    <p>{`Plaid provides a variety of pre-populated test users designed for use with Bank Income and other underwriting-related products.`}</p>
    <Layout.FullWidth>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Username`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Password`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_bank_income`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`{}`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`User with wide variety of income streams of different types`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_credit_bonus`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Any`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Has two salary streams: one with bonuses included in the paycheck, one with bonuses as separate transactions`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_credit_joint_account`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Any`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Has two salary streams and two identities on the account`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_credit_profile_poor`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Any`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Net loss cash flow over time, no consistent source of income`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_credit_profile_good`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Any`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Neutral cash flow over time, multiple gig economy income streams`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_credit_profile_excellent`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Any`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Positive cash flow over time, high salary-based income with secondary rental income stream`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_prism_1`}</inlineCode>{` through `}<inlineCode parentName="td">{`user_prism_8`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Any`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`For testing Partner Insights reports, these users return a variety of consistent partner insights values`}</td>
          </tr>
        </tbody>
      </table>
    </Layout.FullWidth> 
    <p>{`Sandbox Studio also includes custom user templates for testing credit products, such as the Bank Income templates. You can modify these users as you need. For instructions, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/#configuring-the-custom-user-account"
      }}>{`Custom users`}</a>{`.`}</p>
    <p>{`For details on testing Bank Income with custom data, see `}<a parentName="p" {...{
        "href": "/docs/income/bank-income/#testing-bank-income"
      }}>{`Testing Bank Income`}</a>{`.`}</p>
    <h4 {...{
      "id": "liabilities-testing-credentials"
    }}>{`Liabilities testing credentials`}</h4>
    <p>{`Use the username `}<inlineCode parentName="p">{`user_loan_loc`}</inlineCode>{` and password `}<inlineCode parentName="p">{`pass_good`}</inlineCode>{` to create an Item containing general-purpose, commercial, and home-equity loans and lines of credit. Initialize the Item with the `}<inlineCode parentName="p">{`liabilities`}</inlineCode>{` product, then call `}<inlineCode parentName="p">{`/liabilities/get`}</inlineCode>{` to retrieve the pre-populated liability details.`}</p>
    <p>{`In the Dashboard, select `}<strong parentName="p">{`Loans and lines of credit`}</strong>{` from the Sandbox test users or open the `}<strong parentName="p">{`Loan and line of credit liabilities`}</strong>{` Sandbox scenario. When calling `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` directly, use a non-OAuth institution such as First Platypus Bank (`}<inlineCode parentName="p">{`ins_109508`}</inlineCode>{`).`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Liabilities test credentials",
        "switcher": "false",
        "#Liabilities": true,
        "test": true,
        "credentials": true
      }}>{`{
  "institution_id": "ins_109508",
  "initial_products": ["liabilities"],
  "options": {
    "override_username": "user_loan_loc",
    "override_password": "pass_good"
  }
}
`}</code></pre>
    <h4 {...{
      "id": "auth-limited-purpose-checking-test-credentials"
    }}>{`Auth limited purpose checking test credentials`}</h4>
    <p>{`Use the username `}<inlineCode parentName="p">{`user_limited_purpose_checking`}</inlineCode>{` and the password `}<inlineCode parentName="p">{`pass_good`}</inlineCode>{`.`}</p>
    <p>{`For more details, see `}<a parentName="p" {...{
        "href": "/docs/auth/#enabling-limited-purpose-checking-accounts-for-rent-or-mortgage"
      }}>{`Limited purpose checking`}</a>{`.`}</p>
    <h4 {...{
      "id": "monitor-and-identity-verification-testing-users"
    }}>{`Monitor and Identity Verification testing users`}</h4>
    <p>{`Monitor and Identity Verification have product-specific Sandbox users that can be used for testing. For Identity Verification, you can also create custom sample identities in the Plaid Dashboard for more targeted testing scenarios. For more details, see `}<a parentName="p" {...{
        "href": "/docs/monitor/#testing-monitor"
      }}>{`Testing Monitor`}</a>{` and `}<a parentName="p" {...{
        "href": "/docs/identity-verification/testing"
      }}>{`Testing Identity Verification`}</a>{`.`}</p>
    <h4 {...{
      "id": "sandbox-multi-factor-credentials"
    }}>{`Sandbox multi-factor credentials`}</h4>
    <p>{`You can trigger any multi-factor authentication (MFA) flow that Plaid
supports when creating an `}<inlineCode parentName="p">{`Item`}</inlineCode>{` by using username `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` and modifying the password.
These credentials work only in Link at a non-OAuth institution; `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` returns `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` for them.`}</p>
    <p>{`To simulate an institution that presents a multi-factor challenge on every login attempt, use a `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/#simulating-multi-factor-authentication-on-every-login"
      }}>{`custom user`}</a>{` with `}<inlineCode parentName="p">{`has_perpetual_otp`}</inlineCode>{`.`}</p>
    <h5 {...{
      "id": "multi-factor-device-otp"
    }}>{`Multi-factor device OTP`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-python",
        "metastring": "switcher=false #MFA Device OTP credentials",
        "switcher": "false",
        "#MFA": true,
        "Device": true,
        "OTP": true,
        "credentials": true
      }}>{`username: 'user_good'
password: 'mfa_device'

# Code for all devices: 1234
code: 1234
`}</code></pre>
    <h5 {...{
      "id": "multi-factor-questions"
    }}>{`Multi-factor questions`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-python",
        "metastring": "switcher=false #MFA Questions credentials",
        "switcher": "false",
        "#MFA": true,
        "Questions": true,
        "credentials": true
      }}>{`# n-rounds of m-questions per round, where 0 <= i, j < 9
username: 'user_good'
password: 'mfa_questions_<n>_<m>'

# answer_<i>_<j>, for j-th question in i-th round.
answer: 'answer_<i>_<j>'
`}</code></pre>
    <h5 {...{
      "id": "multi-factor-selections"
    }}>{`Multi-factor selections`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-python",
        "metastring": "switcher=false #MFA selections credentials",
        "switcher": "false",
        "#MFA": true,
        "selections": true,
        "credentials": true
      }}>{`username: 'user_good'
password: 'mfa_selections'

# Two questions: answer 'tomato' to the first and 'ketchup' to the second
answers: 'tomato', 'ketchup'
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-python",
        "metastring": "switcher=false #Multiple MFA selections credentials",
        "switcher": "false",
        "#Multiple": true,
        "MFA": true,
        "selections": true,
        "credentials": true
      }}>{`# n-rounds of m-questions with o-answers per question
# 0 < n, m < 10 and 2 <= o < 10
username: 'user_good'
password: 'mfa_selections_<n>_<m>_<o>'

# answer_<i>_<j>_0, for j-th question in i-th round, where 0 <= i < n and 0 <= j < m
answer: 'answer_0_0_0'
`}</code></pre>
    <h4 {...{
      "id": "recaptcha-testing-credentials"
    }}>{`ReCAPTCHA testing credentials`}</h4>
    <p>{`You may trigger a reCAPTCHA in Plaid Link in the Sandbox environment by setting the test account password to a JSON object with a `}<inlineCode parentName="p">{`recaptcha`}</inlineCode>{` field. Setting `}<inlineCode parentName="p">{`recaptcha`}</inlineCode>{` to `}<inlineCode parentName="p">{`good`}</inlineCode>{` will result in successful Item creation, while setting it to `}<inlineCode parentName="p">{`bad`}</inlineCode>{` will result in a `}<inlineCode parentName="p">{`RECAPTCHA_BAD`}</inlineCode>{` error to simulate a failed reCAPTCHA. Both values require the reCAPTCHA to be manually solved within Plaid Link.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python",
        "metastring": "switcher=false #ReCAPTCHA",
        "switcher": "false",
        "#ReCAPTCHA": true
      }}>{`username: user_good
password: {"recaptcha":"bad"}
`}</code></pre>
    <h4 {...{
      "id": "error-testing-credentials"
    }}>{`Error testing credentials`}</h4>
    <p>{`You can trigger a number of the errors that you may encounter when creating an Item by using username `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` and modifying the password.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python",
        "metastring": "switcher=false #Error code password",
        "switcher": "false",
        "#Error": true,
        "code": true,
        "password": true
      }}>{`error_[ERROR_CODE]
`}</code></pre>
    <p>{`For example, the password `}<inlineCode parentName="p">{`error_ITEM_LOCKED`}</inlineCode>{` allows you to simulate an `}<inlineCode parentName="p">{`ITEM_LOCKED`}</inlineCode>{` error.`}</p>
    <p>{`Note that error testing credentials are not compatible with institutions that use OAuth. If you are not sure whether an institution uses OAuth flows, see the list of `}<a parentName="p" {...{
        "href": "/docs/sandbox/institutions/"
      }}>{`Sandbox institutions`}</a>{` to find an institution that is guaranteed not to use OAuth. `}</p>
    <p>{`The list of errors that can be simulated in this way is below:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Errors available for testing",
        "#Errors": true,
        "available": true,
        "for": true,
        "testing": true
      }}>{`"ACCESS_NOT_GRANTED"
"COUNTRY_NOT_SUPPORTED"
"INSTITUTION_NOT_RESPONDING"
"INSTITUTION_NO_LONGER_SUPPORTED"
"INSTITUTION_REGISTRATION_REQUIRED"
"INSUFFICIENT_CREDENTIALS"
"INTERNAL_SERVER_ERROR"
"INVALID_CREDENTIALS"
"INVALID_MFA"
"INVALID_SEND_METHOD"
"ITEM_LOCKED"
"ITEM_NOT_SUPPORTED"
"MFA_NOT_SUPPORTED"
"NO_ACCOUNTS"
"PAYMENT_INSUFFICIENT_FUNDS"
"PAYMENT_INVALID_RECIPIENT"
"PAYMENT_INVALID_REFERENCE"
"PAYMENT_INVALID_SCHEDULE"
"PAYMENT_REJECTED"
"PAYMENT_SCHEME_NOT_SUPPORTED"
"UNAUTHORIZED_INSTITUTION"
"USER_INPUT_TIMEOUT"
"USER_SETUP_REQUIRED"
"USER_SHOULD_VERIFY_DEPOSIT_ALLOCATIONS"
`}</code></pre>
    <p>{`You can test several other OAuth-specific Link errors via the drop-down selector on the First Platypus OAuth screen after selecting any OAuth institution. The list of errors that can be simulated in this way is below:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#OAuth errors available for testing",
        "#OAuth": true,
        "errors": true,
        "available": true,
        "for": true,
        "testing": true
      }}>{`"INSUFFICIENT_CREDENTIALS"
"ACCESS_NOT_GRANTED"
"NO_AUTH_ACCOUNTS"
`}</code></pre>
    <h4 {...{
      "id": "link-recovery-testing-credentials"
    }}>{`Link Recovery testing credentials`}</h4>
    <p>{`See `}<a parentName="p" {...{
        "href": "/docs/link/link-recovery/#testing-link-recovery"
      }}>{`Testing Link Recovery`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "sandbox-simple-test-credentials",
        level: 4,
        title: "Sandbox simple test credentials",
      },
{
        id: "transactions-testing-credentials",
        level: 4,
        title: "Transactions testing credentials",
      },
{
        id: "auth-micro-deposit-testing-credentials",
        level: 4,
        title: "Auth micro-deposit testing credentials",
      },
{
        id: "credit-and-income-testing-credentials",
        level: 4,
        title: "Credit and Income testing credentials",
      },
{
        id: "liabilities-testing-credentials",
        level: 4,
        title: "Liabilities testing credentials",
      },
{
        id: "auth-limited-purpose-checking-test-credentials",
        level: 4,
        title: "Auth limited purpose checking test credentials",
      },
{
        id: "monitor-and-identity-verification-testing-users",
        level: 4,
        title: "Monitor and Identity Verification testing users",
      },
{
        id: "sandbox-multi-factor-credentials",
        level: 4,
        title: "Sandbox multi-factor credentials",
      },
{
        id: "recaptcha-testing-credentials",
        level: 4,
        title: "ReCAPTCHA testing credentials",
      },
{
        id: "error-testing-credentials",
        level: 4,
        title: "Error testing credentials",
      },
{
        id: "link-recovery-testing-credentials",
        level: 4,
        title: "Link Recovery testing credentials",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
